import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// Breathing room kept between the menu and the edge of the window.
const VIEWPORT_MARGIN = 8;
// Distance between the trigger and the menu.
const ANCHOR_GAP = 4;

/**
 * Places a pop-up menu next to its trigger using viewport coordinates.
 *
 * The menu is positioned `fixed` rather than `absolute` on purpose: the tables
 * these menus live in set `overflow: hidden` (for their rounded corners), which
 * clips absolutely positioned children no matter how high their z-index is. A
 * fixed element is laid out against the viewport, so it escapes that clipping.
 */
const useCalculateMenuPosition = (isOpen) => {
  const menuRef = useRef(null);
  const [coords, setCoords] = useState(null);

  const calculatePosition = useCallback(() => {
    const menu = menuRef.current;
    // The trigger is whatever the menu sits next to in the markup.
    const anchor = menu?.parentElement;

    if (!menu || !anchor) return;

    const anchorRect = anchor.getBoundingClientRect();
    const menuWidth = menu.offsetWidth;
    const menuHeight = menu.offsetHeight;
    const { innerWidth, innerHeight } = window;

    // Line the menu up with the trigger's left edge, and flip it to hang off
    // the right edge instead when that would run past the window.
    let left = anchorRect.left;
    if (left + menuWidth > innerWidth - VIEWPORT_MARGIN) {
      left = anchorRect.right - menuWidth;
    }
    left = Math.min(Math.max(left, VIEWPORT_MARGIN), innerWidth - menuWidth - VIEWPORT_MARGIN);

    // Below the trigger, or above it when the bottom of the window is close.
    let top = anchorRect.bottom + ANCHOR_GAP;
    if (top + menuHeight > innerHeight - VIEWPORT_MARGIN) {
      const above = anchorRect.top - menuHeight - ANCHOR_GAP;
      top = above >= VIEWPORT_MARGIN ? above : innerHeight - menuHeight - VIEWPORT_MARGIN;
    }
    top = Math.max(top, VIEWPORT_MARGIN);

    top = top - 10;

    setCoords({ top: Math.round(top), left: Math.round(left) });
  }, []);

  // Measured before paint, so the menu is never seen in the wrong place.
  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }

    calculatePosition();
  }, [isOpen, calculatePosition]);

  // A fixed menu does not travel with its trigger, so follow it when the page
  // or any scrollable container under it moves. Capture catches inner scrollers.
  useEffect(() => {
    if (!isOpen) return undefined;

    const repositionHandler = () => calculatePosition();

    window.addEventListener("scroll", repositionHandler, true);
    window.addEventListener("resize", repositionHandler);

    return () => {
      window.removeEventListener("scroll", repositionHandler, true);
      window.removeEventListener("resize", repositionHandler);
    };
  }, [isOpen, calculatePosition]);

  return {
    menuRef,
    // Fixed from the first render so the menu is measured the way it is shown.
    style: { position: "fixed", top: coords?.top ?? 0, left: coords?.left ?? 0 },
    ready: coords !== null,
  };
};

export default useCalculateMenuPosition;
